import { useState, useEffect } from 'react';
import { info, error as logError } from '../../../utils/logger';

const EmailSettings = () => {
  const [formData, setFormData] = useState({
    emailProvider: 'smtp',
    smtpHost: 'smtp.example.com',
    smtpPort: '587',
    smtpUsername: 'user@example.com',
    smtpPassword: '',
    smtpEncryption: 'tls',
    senderEmail: 'noreply@prokidsapp.com',
    senderName: 'ProKidsApp',
    emailFooter: 'ProKidsApp - Todos los derechos reservados',
    emailLogo: '/logo.png',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [showTestEmail, setShowTestEmail] = useState(false);
  const [testEmailSent, setTestEmailSent] = useState(false);
  const [testEmailLoading, setTestEmailLoading] = useState(false);

  useEffect(() => {
    // En una implementación real, obtendríamos estos datos de la API
    // Por ahora, usamos los datos de ejemplo definidos en el estado inicial
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
    }, 500);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccess(false);
    setError(null);

    try {
      // En una implementación real, enviaríamos estos datos a la API
      info('Guardando configuración de email', {
        provider: formData.emailProvider,
        senderEmail: formData.senderEmail
      });

      // Simulamos una respuesta exitosa
      setTimeout(() => {
        setSuccess(true);
        setLoading(false);
      }, 1000);
    } catch (error) {
      logError('Error saving email settings', {
        error: error.message,
        stack: error.stack
      });
      setError('Error al guardar la configuración');
      setLoading(false);
    }
  };

  const handleTestEmail = (e) => {
    e.preventDefault();
    setTestEmailLoading(true);
    setTestEmailSent(false);

    try {
      // En una implementación real, enviaríamos un correo de prueba
      info('Enviando correo de prueba', {
        testEmail: testEmailAddress,
        provider: formData.emailProvider
      });

      // Simulamos una respuesta exitosa
      setTimeout(() => {
        setTestEmailSent(true);
        setTestEmailLoading(false);
        setShowTestEmail(false);
      }, 1500);
    } catch (error) {
      logError('Error sending test email', {
        error: error.message,
        testEmail: testEmailAddress,
        stack: error.stack
      });
      setError('Error al enviar el correo de prueba');
      setTestEmailLoading(false);
    }
  };

  if (loading && Object.keys(formData).length === 0) {
    return <div className="loading">Cargando configuración...</div>;
  }

  return (
    <div className="settings-section">
      <h2>Configuración de Correo Electrónico</h2>
      <p className="settings-description">
        Configura los ajustes de correo electrónico para el envío de notificaciones.
      </p>

      {success && <div className="alert alert-success">Configuración guardada correctamente.</div>}

      {testEmailSent && (
        <div className="alert alert-success">
          Correo de prueba enviado correctamente a {testEmailAddress}.
        </div>
      )}

      {error && <div className="alert alert-danger">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="settings-card">
          <h3>Proveedor de Correo</h3>
          <div className="form-group">
            <label htmlFor="emailProvider">Proveedor</label>
            <select
              id="emailProvider"
              name="emailProvider"
              value={formData.emailProvider}
              onChange={handleInputChange}
              className="form-control"
            >
              <option value="smtp">SMTP</option>
              <option value="sendgrid">SendGrid</option>
              <option value="mailgun">Mailgun</option>
              <option value="ses">Amazon SES</option>
            </select>
          </div>
        </div>

        {formData.emailProvider === 'smtp' && (
          <div className="settings-card">
            <h3>Configuración SMTP</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="smtpHost">Servidor SMTP</label>
                <input
                  type="text"
                  id="smtpHost"
                  name="smtpHost"
                  value={formData.smtpHost}
                  onChange={handleInputChange}
                  className="form-control"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="smtpPort">Puerto SMTP</label>
                <input
                  type="text"
                  id="smtpPort"
                  name="smtpPort"
                  value={formData.smtpPort}
                  onChange={handleInputChange}
                  className="form-control"
                  required
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="smtpUsername">Usuario SMTP</label>
                <input
                  type="text"
                  id="smtpUsername"
                  name="smtpUsername"
                  value={formData.smtpUsername}
                  onChange={handleInputChange}
                  className="form-control"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="smtpPassword">Contraseña SMTP</label>
                <input
                  type="password"
                  id="smtpPassword"
                  name="smtpPassword"
                  value={formData.smtpPassword}
                  onChange={handleInputChange}
                  className="form-control"
                  placeholder="••••••••"
                />
                <small className="form-text">
                  Dejar en blanco para mantener la contraseña actual
                </small>
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="smtpEncryption">Cifrado</label>
              <select
                id="smtpEncryption"
                name="smtpEncryption"
                value={formData.smtpEncryption}
                onChange={handleInputChange}
                className="form-control"
              >
                <option value="tls">TLS</option>
                <option value="ssl">SSL</option>
                <option value="none">Ninguno</option>
              </select>
            </div>
          </div>
        )}

        <div className="settings-card">
          <h3>Remitente</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="senderEmail">Email del Remitente</label>
              <input
                type="email"
                id="senderEmail"
                name="senderEmail"
                value={formData.senderEmail}
                onChange={handleInputChange}
                className="form-control"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="senderName">Nombre del Remitente</label>
              <input
                type="text"
                id="senderName"
                name="senderName"
                value={formData.senderName}
                onChange={handleInputChange}
                className="form-control"
                required
              />
            </div>
          </div>
        </div>

        <div className="settings-card">
          <h3>Personalización de Correos</h3>
          <div className="form-group">
            <label htmlFor="emailLogo">URL del Logo para Correos</label>
            <input
              type="text"
              id="emailLogo"
              name="emailLogo"
              value={formData.emailLogo}
              onChange={handleInputChange}
              className="form-control"
            />
          </div>
          <div className="form-group">
            <label htmlFor="emailFooter">Pie de Página para Correos</label>
            <textarea
              id="emailFooter"
              name="emailFooter"
              value={formData.emailFooter}
              onChange={handleInputChange}
              className="form-control"
              rows="2"
            ></textarea>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowTestEmail(true)}
          >
            Enviar Correo de Prueba
          </button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Guardando...' : 'Guardar Cambios'}
          </button>
        </div>
      </form>

      {showTestEmail && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Enviar Correo de Prueba</h2>
              <button className="modal-close" onClick={() => setShowTestEmail(false)}>
                <span className="material-icons">close</span>
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleTestEmail}>
                <div className="form-group">
                  <label htmlFor="testEmailAddress">Dirección de Correo</label>
                  <input
                    type="email"
                    id="testEmailAddress"
                    value={testEmailAddress}
                    onChange={(e) => setTestEmailAddress(e.target.value)}
                    className="form-control"
                    placeholder="ejemplo@correo.com"
                    required
                  />
                </div>
                <div className="form-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowTestEmail(false)}
                  >
                    Cancelar
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={testEmailLoading}>
                    {testEmailLoading ? 'Enviando...' : 'Enviar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailSettings;
